/* ════════════════════════════════════════════════
   Bottom sheets on phones (menu, tooltips, bet panel): one small helper
   for what every sheet needs while it is open (team#159):
   - announced as a dialog (role="dialog", aria-modal, a label)
   - the page behind it doesn't scroll, and stays where it was
   - focus moves into the sheet, Tab stays inside it, and closing
     returns focus to whatever opened it
   - Esc closes it (when the caller passes onEscape)
   ════════════════════════════════════════════════ */

var FOCUSABLE = 'a[href], button, input, select, textarea, details > summary:first-of-type, [tabindex]';

var locks = 0;
var saved = null;

// Scroll lock. overflow:hidden on <body> passes to the viewport (as long as
// <html> leaves overflow alone), so the page stops scrolling, keeps its
// position, and the sticky header stays put. A classic desktop scrollbar would
// disappear with it and widen the page (the header's ☰ would jump sideways),
// so <html> keeps its gutter. Phones use overlay scrollbars and have none.
export function lockScroll() {
  if (locks++ > 0) return;
  var html = document.documentElement;
  saved = { overflow: document.body.style.overflow, gutter: html.style.scrollbarGutter };
  if (window.innerWidth - html.clientWidth > 0) html.style.scrollbarGutter = 'stable';
  document.body.style.overflow = 'hidden';
}

export function unlockScroll() {
  if (locks === 0 || --locks > 0) return;
  document.body.style.overflow = saved.overflow;
  document.documentElement.style.scrollbarGutter = saved.gutter;
  saved = null;
}

// The items Tab can actually reach: not disabled or tabindex="-1", and not
// hidden by the item itself or by anything between it and the sheet (hidden,
// inert, aria-hidden, display:none, visibility:hidden). Inside a closed
// <details> only its summary row can be reached, not the links under it, so
// "last item" is the last row you can really get to (team#160).
function reachable(el, sheet) {
  if (el.tabIndex < 0 || el.matches(':disabled') || window.getComputedStyle(el).visibility === 'hidden') return false;
  for (var node = el; node !== sheet; node = node.parentElement) {
    if (node.hidden || node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true' ||
        window.getComputedStyle(node).display === 'none') return false;
    var box = node.parentElement;
    if (box.tagName === 'DETAILS' && !box.open && node !== box.querySelector(':scope > summary')) return false;
  }
  return true;
}

function focusables(sheet) {
  return Array.prototype.filter.call(sheet.querySelectorAll(FOCUSABLE), function (el) { return reachable(el, sheet); });
}

// Open `sheet` as a modal dialog. Returns release(restoreFocus = true): call it
// when the sheet closes. Options:
//   label       accessible name (aria-label)
//   opener      element to return focus to (default: the focused element)
//   onEscape    called on Esc (leave out if the caller already handles Esc)
//   scrim       backdrop element: touch-drags on it never scroll the page
export function openSheet(sheet, opts) {
  opts = opts || {};
  var opener = opts.opener || document.activeElement;
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  if (opts.label) sheet.setAttribute('aria-label', opts.label);
  if (!sheet.hasAttribute('tabindex')) sheet.setAttribute('tabindex', '-1');
  lockScroll();

  function onKey(e) {
    if (e.key === 'Escape' && opts.onEscape) {
      e.preventDefault();
      opts.onEscape();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusables(sheet);
    if (!items.length) { e.preventDefault(); sheet.focus(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || active === sheet || !sheet.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !sheet.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }
  // Anything that moves focus out (a click on the page behind, a screen
  // reader's virtual cursor) brings it back into the sheet
  function onFocusIn(e) {
    if (sheet.contains(e.target)) return;
    (focusables(sheet)[0] || sheet).focus({ preventScroll: true });
  }
  function noScroll(e) { e.preventDefault(); }

  document.addEventListener('keydown', onKey, true);
  document.addEventListener('focusin', onFocusIn);
  if (opts.scrim) opts.scrim.addEventListener('touchmove', noScroll, { passive: false });
  (focusables(sheet)[0] || sheet).focus({ preventScroll: true });

  var released = false;
  return function release(restoreFocus) {
    if (released) return;
    released = true;
    document.removeEventListener('keydown', onKey, true);
    document.removeEventListener('focusin', onFocusIn);
    if (opts.scrim) opts.scrim.removeEventListener('touchmove', noScroll);
    unlockScroll();
    if (restoreFocus !== false && opener && opener.focus && document.contains(opener) && !sheet.contains(opener)) {
      opener.focus({ preventScroll: true });
    } else if (sheet.contains(document.activeElement)) {
      // The opener is gone (the list re-rendered): never leave focus on a sheet that is about to be hidden
      document.activeElement.blur();
    }
  };
}

// Scripts loaded without modules (betting.js) reach the helper here
window.TLSheet = { openSheet: openSheet, lockScroll: lockScroll, unlockScroll: unlockScroll };
